import {createSelector} from 'reselect'
import isEqual from 'lodash/isEqual.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
} from '../../../../store.js'
import {TAG_FILTER_OR} from '../../../../common/constants/Tags.js'
import {clearTagFilterPanelSearch} from './TagFilterPanel.js'
import {
  cleanPOQuery,
  poListQueryToPOQuery,
  summarizePODateRangeParams,
} from '../../purchaseOrderListSelectors.js'
import {
  navigatePOList,
  updatePOExcludeTags,
  updatePOTagFilters,
  updateSearchText,
  updateStatus,
  updatePOTagFilterBy,
  updatePOUntagged,
  updateSupplierFilters,
  updateWarehouseFilters,
  updatePODateRangeFilters,
  updateSKUFilters,
  updatePOIDFilters,
} from '../../purchaseOrderListActions.js'
import {clearFilterPanelSearch} from '../../../OrderListPage/Modals/OrderListFilterModal/FilterPanel.js'
import {PO_DEFAULT_DATE_RANGE_TYPE} from '../../../../common/constants/PurchaseOrderListDateRangeFilters.js'
import {
  REPORT_INTERVAL_MONTH,
  REPORT_INTERVALS,
} from '../../../../common/constants/Reports.js'
import deferPromise from '../../../../common/deferPromise.js'
import {getTimeZone} from '../../../../common/date.js'
import {
  isNonZeroPositiveInteger,
  isNonZeroPositiveNumeric,
} from '../../../../common/utils.js'

const MODAL_FORM = 'po_list_filter_modal'

export const STATUS_FILTER_PANEL = 'status'
export const TAG_FILTER = 'tag'
export const SUPPLIER_FILTER_PANEL = 'supplier'
export const WAREHOUSE_FILTER_PANEL = 'warehouse'
export const DATE_RANGE_FILTER_PANEL = 'date_range'
export const SKU_FILTER_PANEL = 'sku'
export const PO_ID_FILTER_PANEL = 'po_id'

export const PO_LIST_FILTER_OPTIONS = [
  {value: STATUS_FILTER_PANEL, display: 'Status'},
  {value: TAG_FILTER, display: 'Tags'},
  {value: WAREHOUSE_FILTER_PANEL, display: 'Warehouses'},
  {value: SUPPLIER_FILTER_PANEL, display: 'Suppliers'},
  {value: DATE_RANGE_FILTER_PANEL, display: 'Date Range'},
  {value: SKU_FILTER_PANEL, display: 'SKUs'},
  {value: PO_ID_FILTER_PANEL, display: 'PO IDs'},
]

export async function showPOListFilterModal({query = {}} = {}) {
  const {updates} = await showPOListFilterModalPrime({
    query,
    singleRun: true,
  })

  return await navigatePOList(updates)
}

function getAPIQuery(originalForm, updates) {
  const poListQuery = {...originalForm, ...updates}

  const poQuery = cleanPOQuery(poListQueryToPOQuery(poListQuery))

  delete poQuery.sort
  delete poQuery.limit
  delete poQuery.offset

  if (!poListQuery.includeTimezone) {
    delete poQuery.timezone
  }

  return poQuery
}

export async function showPOQueryFilterModal({
  query = {},
  singleRun = true,
} = {}) {
  const {originalForm, updates} = await showPOListFilterModalPrime({
    query,
    singleRun,
  })

  if (!updates) {
    return query
  }

  return getAPIQuery(originalForm, updates)
}

export async function showPOListFilterModalPrime({
  query = {},
  singleRun = true,
  deferredPromise = deferPromise(),
  originalForm,
} = {}) {
  const {
    startDate,
    endDate,
    dateFilterType,
    intervalType,
    intervalAmount,
    intervalTrunc,
  } = summarizePODateRangeParams(query)

  const form = {
    searchText: query.search || '',
    status: query.status || [],
    tags: query.tag || [],
    untagged: query.untagged || false,
    tag_filter_by: query.tag_filter_by || TAG_FILTER_OR,
    exclude_tags: query.exclude_tags || [],
    supplier_id: query.supplier_id || [],
    warehouse_id: query.warehouse_id || [],
    sku: query.sku || [],
    po_id: query.po_id || [],
    dateFilterType: dateFilterType || PO_DEFAULT_DATE_RANGE_TYPE,
    startDate: startDate || null,
    endDate: endDate || null,
    intervalType: intervalType || (!singleRun ? REPORT_INTERVALS[0] : ''),
    intervalAmount: intervalAmount || '1',
    intervalTrunc: intervalTrunc || false,
    timezone: getTimeZone(),
    includeTimezone: false,
  }

  setForm(MODAL_FORM, {
    singleRun,
    deferredPromise,
    originalForm: originalForm || {...form},
    ...form,
  })

  // timezone is sticky so override its sticky value applied in setForm()
  if (query.timezone) {
    updateModalForm({timezone: query.timezone, includeTimezone: true})
  }

  clearFilterPanelSearch(SUPPLIER_FILTER_PANEL)
  clearFilterPanelSearch(WAREHOUSE_FILTER_PANEL)
  clearTagFilterPanelSearch()

  return deferredPromise.promise
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal(updates) {
  const {originalForm, deferredPromise} = modalFormSelector(getState())

  deferredPromise.resolve({originalForm, updates})

  removeForm(MODAL_FORM)
}

export function clearFilters() {
  const {singleRun, deferredPromise, originalForm} =
    modalFormSelector(getState())

  // re-setup modal form with most of the pieces reused
  // reuse deferredPromise so that any awaits from the original call to show... will resolve
  // reuse originalForm so applyFilter() has the correct thing to compare against
  // reuse singleRun because that is part of the context
  showPOListFilterModalPrime({
    singleRun,
    deferredPromise,
    originalForm,
  })
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({intervalType, intervalAmount}) => {
    const errors = {}

    if (intervalType) {
      if (
        intervalType === REPORT_INTERVAL_MONTH &&
        !isNonZeroPositiveInteger(intervalAmount)
      ) {
        errors.intervalAmount = 'Amount must be a positive whole number'
        errors.preventSave = true
      } else if (
        intervalType !== REPORT_INTERVAL_MONTH &&
        !isNonZeroPositiveNumeric(intervalAmount)
      ) {
        errors.intervalAmount = 'Amount must be a positive number'
        errors.preventSave = true
      }
    }

    return errors
  },
)

export async function applyFilter() {
  const {
    originalForm,
    searchText,
    status,
    tags,
    untagged,
    tag_filter_by,
    exclude_tags,
    supplier_id,
    warehouse_id,
    sku,
    po_id,
    dateFilterType,
    startDate,
    endDate,
    intervalType,
    intervalAmount,
    intervalTrunc,
    timezone,
    includeTimezone,
  } = modalFormSelector(getState())

  let updates = {}

  if (searchText !== originalForm.searchText) {
    updates = {...updates, ...updateSearchText(searchText)}
  }

  if (!isEqual(status, originalForm.status)) {
    updates = {...updates, ...updateStatus(status)}
  }

  if (!isEqual(tags, originalForm.tags)) {
    updates = {...updates, ...updatePOTagFilters(tags)}
  }

  if (untagged !== originalForm.untagged) {
    updates = {...updates, ...updatePOUntagged(untagged)}
  }

  if (tag_filter_by !== originalForm.tag_filter_by) {
    updates = {...updates, ...updatePOTagFilterBy(tag_filter_by)}
  }

  if (!isEqual(exclude_tags, originalForm.exclude_tags)) {
    updates = {...updates, ...updatePOExcludeTags(exclude_tags)}
  }

  if (!isEqual(supplier_id, originalForm.supplier_id)) {
    updates = {...updates, ...updateSupplierFilters(supplier_id)}
  }

  if (!isEqual(warehouse_id, originalForm.warehouse_id)) {
    updates = {...updates, ...updateWarehouseFilters(warehouse_id)}
  }

  if (!isEqual(sku, originalForm.sku)) {
    updates = {...updates, ...updateSKUFilters(sku)}
  }

  if (!isEqual(po_id, originalForm.po_id)) {
    updates = {...updates, ...updatePOIDFilters(po_id)}
  }

  if (
    dateFilterType !== originalForm.dateFilterType ||
    startDate !== originalForm.startDate ||
    endDate !== originalForm.endDate ||
    intervalType !== originalForm.intervalType ||
    intervalAmount !== originalForm.intervalAmount ||
    intervalTrunc !== originalForm.intervalTrunc ||
    timezone !== originalForm.timezone ||
    includeTimezone !== originalForm.includeTimezone
  ) {
    updates = {
      ...updates,
      ...updatePODateRangeFilters({
        dateFilterType,
        startDate,
        endDate,
        intervalType,
        intervalAmount,
        intervalTrunc,
        timezone: includeTimezone ? timezone : undefined,
      }),
    }
  }

  closeModal(updates)
}
